import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface ImageLightboxProps {
  src: string;
  onClose: () => void;
}

export default function ImageLightbox({ src, onClose }: ImageLightboxProps) {
  // Lock body scroll while lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const pinchRef = useRef<{ startDist: number; startScale: number } | null>(null);
  const panRef = useRef<{
    startX: number;
    startY: number;
    startTx: number;
    startTy: number;
  } | null>(null);
  const lastTapRef = useRef(0);
  const imgRef = useRef<HTMLDivElement>(null);

  const resetTransform = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        // Pinch start
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchRef.current = { startDist: Math.hypot(dx, dy), startScale: scale };
        panRef.current = null;
      } else if (e.touches.length === 1 && scale > 1) {
        // Pan start (only when zoomed)
        panRef.current = {
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startTx: translate.x,
          startTy: translate.y,
        };
      }
    },
    [scale, translate],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        const newScale = Math.min(
          5,
          Math.max(1, pinchRef.current.startScale * (dist / pinchRef.current.startDist)),
        );
        setScale(newScale);
        if (newScale <= 1) setTranslate({ x: 0, y: 0 });
      } else if (e.touches.length === 1 && panRef.current && scale > 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - panRef.current.startX;
        const dy = e.touches[0].clientY - panRef.current.startY;
        setTranslate({ x: panRef.current.startTx + dx, y: panRef.current.startTy + dy });
      }
    },
    [scale],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      pinchRef.current = null;
      panRef.current = null;

      // Double-tap to zoom
      if (e.touches.length === 0 && e.changedTouches.length === 1) {
        const now = Date.now();
        if (now - lastTapRef.current < 300) {
          if (scale > 1) {
            resetTransform();
          } else {
            setScale(2.5);
          }
          lastTapRef.current = 0;
        } else {
          lastTapRef.current = now;
        }
      }

      // Snap back to 1x if barely zoomed
      if (scale <= 1.05) resetTransform();
    },
    [scale, resetTransform],
  );

  const handleBackdropClick = useCallback(
    (_e: React.MouseEvent) => {
      if (scale > 1) {
        resetTransform();
      } else {
        onClose();
      }
    },
    [scale, resetTransform, onClose],
  );

  return (
    <div
      className="fixed inset-0 bg-black/80 z-60 flex items-center justify-center p-8 overscroll-none"
      onClick={handleBackdropClick}
      onTouchMove={(e) => {
        // Prevent background scroll, but let image touch handlers work
        if (e.target === e.currentTarget) e.preventDefault();
      }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 size-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors z-10"
      >
        <X size={20} className="text-white" />
      </button>
      <div
        ref={imgRef}
        className="max-w-full max-h-full"
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transition: pinchRef.current || panRef.current ? 'none' : 'transform 0.2s ease-out',
          touchAction: 'none',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt="Attachment preview"
          className="max-w-full max-h-full rounded-xl object-contain select-none"
          draggable={false}
        />
      </div>
    </div>
  );
}
